// src/pages/TranslationListPage.tsx
import { BaseTable } from "@/components/BaseTable";
import CustomButton from "@/components/common/Button";
import { TranslationService } from "@/services/APIs";
import { gridName } from "@/utils/constant/gridName";
import navigationRoutes from "@/utils/navigationRoutes";
import { Translation } from "@/utils/types/translationHooks";
import { ProColumns } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Popconfirm } from "antd";
import { Edit2, Plus, Trash } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ActiveToggleButton from "../../components/ActiveToggleButton";
import { tableName } from "../../utils/constant/tableName";

const TranslationListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPage = parseInt(searchParams.get("page") ?? "1", 10);
  const urlPageSize = parseInt(searchParams.get("pageSize") ?? "10", 10);
  const [page, setPage] = useState(urlPage);
  const [pageSize, setPageSize] = useState(urlPageSize);
  const {
    data,
    isLoading,
    refetch: refetchTranslations,
  } = useQuery({
    queryKey: ["translations", page, pageSize],
    queryFn: () => TranslationService.listTranslations(page, pageSize),
  });

  const handleEdit = (id: string) => {
    navigate(navigationRoutes.TRANSLATION_EDIT(id));
  };

  const handleDelete = async (id: string) => {
    const response = await TranslationService.deleteTranslation(id);
    if (response) {
      refetchTranslations();
    }
  };

  const columns: ProColumns<Translation>[] = [
    {
      title: "Language",
      dataIndex: "language",
      sorter: (a, b) => a.language!.localeCompare(b.language!),
    },
    {
      title: "Source Text",
      dataIndex: "source_text",
    },
    {
      title: "Translated Text",
      dataIndex: "translated_text",
    },
    {
      title: "Is Active",
      width: 120,
      render: (record: any) => {
        return (
          <ActiveToggleButton
            table={tableName.translations}
            pk={record.id}
            is_active={record.is_active}
          />
        );
      },
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          onClick={() => handleEdit(String(record.id))}
          icon={<Edit2 size={16} />}
          type="text"
          title="Edit Translation"
        />,
        <Popconfirm
          key="delete"
          title={`Delete translation "${record.source_text}"?`}
          onConfirm={() => handleDelete(String(record.id))}
          okText="Yes"
          cancelText="No"
        >
          <CustomButton
            danger
            icon={<Trash size={16} />}
            type="text"
            title="Delete Translation"
          />
        </Popconfirm>,
      ],
    },
  ];

  const toolBarRender = () => [
    <CustomButton
      key="add"
      type="primary"
      icon={<Plus size={16} />}
      onClick={() => navigate(navigationRoutes.TRANSLATION_ADD)}
      title="Add New Translation"
    >
      Add Translation
    </CustomButton>,
  ];

  return (
    <BaseTable<Translation>
      loading={isLoading}
      columns={columns}
      dataSource={data?.results ?? []}
      page={page}
      pageSize={pageSize}
      total={data?.count}
      onPageChange={(page, pageSize) => {
        setPage(page);
        setPageSize(pageSize ?? 10);
        searchParams.set("page", page.toString());
        searchParams.set("pageSize", pageSize.toString());
        navigate(`${navigationRoutes.TRANSLATIONS}?${searchParams.toString()}`);
        refetchTranslations();
      }}
      toolBarRender={toolBarRender}
      rowKey="id"
      headerTitle="Translations List"
      request={() => refetchTranslations()}
      tableName={gridName.translationTable}
    />
  );
};

export default TranslationListPage;
